import mapboxGeocoding from '@mapbox/mapbox-sdk/services/geocoding'
import get from 'lodash/get'
import { LngLat, Marker } from 'mapbox-gl'
import * as React from 'react'
import { useEffect, useRef } from 'react'
import { useMap } from '../mapbox/MapContext'
import NavigationControl from '../mapbox/NavigationControl'
import { connectField } from 'uniforms'
import { HiddenField } from 'uniforms-unstyled'
import { DEFAULT_MAP_CENTER_COORDINATES, MAPBOX_ACCESS_TOKEN } from '../../../util/constants'
import Mapbox from '../mapbox/Mapbox'

const geocoding = mapboxGeocoding({ accessToken: MAPBOX_ACCESS_TOKEN })

const DraggableMarker = ({ setCenter, center }) => {
  const map = useMap()
  const marker = useRef()
  if (!marker.current) {
    marker.current = new Marker({ draggable: true })
  }

  // update the marker position
  useEffect(() => {
    marker.current.setLngLat(center)
  }, [center])

  // update the map center whenever the marker is moved
  useEffect(() => {
    const callback = () => setCenter(marker.current.getLngLat())
    marker.current.on('dragend', callback)

    return () => marker.current.off('dragend', callback)
  }, [setCenter])

  // add the marker to the map
  useEffect(() => {
    if (!map) return

    marker.current.addTo(map)

    return () => marker.current.remove()
  }, [map])

  return null
}

const mapStyle = 'mapbox://styles/mapbox/satellite-streets-v11?optimize=true'
const zoomLevel = 16
const mapOptions = { scrollZoom: false }
const flyToOptions = { duration: 800 }
const geocodingDelay = 1000
// Mapbox returns a loose guess for a made-up address instead of no match. Results scoring
// below this are treated as "not found". Chosen from live lookups (CVL-75): real and
// misspelled addresses scored 0.89–1, made-up ones 0.33–0.82.
const minRelevance = 0.85
// Only a house-level address (a house number on an "address" result) is trusted. A made-up
// street in a real town comes back as a street, ZIP or town, often scoring well (CVL-75).
const isTrustedMatch = match =>
  Boolean(match) && match.relevance >= minRelevance && Boolean(match.address) && match.place_type?.includes('address')

const normalizeTown = (town: unknown) =>
  String(town || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')

// edit distance counting a swap of two neighbouring letters as one edit ("Hartfrod" -> "Hartford")
const editDistance = (a: string, b: string) => {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)])
  for (let j = 1; j <= b.length; j++) d[0][j] = j
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost)
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1)
      }
    }
  }
  return d[a.length][b.length]
}

// A one-letter typo ("Mystc") still matches; a different town ("Old Lyme" vs "Lyme") does not.
const sameTown = (typed: string, found: string) => editDistance(typed, found) <= 1

const contextTexts = (match, type: string) =>
  (match.context || []).filter(({ id }) => String(id).startsWith(`${type}.`)).map(({ text }) => text)

// Street names compared word by word, with common abbreviations spelled the same way
// ("S Broad St" and "South Broad Street" match). Mapbox spells the suffix out, so the
// common USPS suffix abbreviations are listed.
const streetAbbreviations = {
  road: 'rd',
  street: 'st',
  boulevard: 'blvd',
  avenue: 'ave',
  highway: 'hwy',
  lane: 'ln',
  drive: 'dr',
  court: 'ct',
  place: 'pl',
  terrace: 'ter',
  circle: 'cir',
  trail: 'trl',
  parkway: 'pkwy',
  turnpike: 'tpke',
  route: 'rte',
  extension: 'ext',
  square: 'sq',
  hill: 'hl',
  crossing: 'xing',
  point: 'pt',
  center: 'ctr',
  heights: 'hts',
  south: 's',
  north: 'n',
  east: 'e',
  west: 'w',
}
// unit text after the street ("Apt 2", "#3", ", Suite 100") is not part of the street
const unitSuffix = /[\s,]+(?:#|(?:apt|apartment|unit|suite|ste|fl|floor)\b\.?)\s*(?:[\w-]*\d[\w-]*|[a-z])$/
const normalizeStreet = (street: unknown) =>
  normalizeTown(street)
    .replace(unitSuffix, '')
    .replace(/[.,]/g, '')
    .split(' ')
    .map(word => streetAbbreviations[word] || word)
    .join(' ')

// the street typed, without its house number, against the street Mapbox returns in `text`
const sameStreet = (match, typedStreetAddress: unknown) => {
  const typedStreet = normalizeStreet(typedStreetAddress).replace(/^\d\S*\s+/, '')
  return Boolean(typedStreet) && typedStreet === normalizeStreet(match.text)
}

// a ZIP+4 ("06382-1234") is compared by its first 5 digits
const normalizeZip = (zip: unknown) => String(zip || '').trim().slice(0, 5)

// Mapbox can return a confident house-level address in another town (CVL-77). The match is
// rejected when the town typed clearly differs from the result's town (Lyme and Old Lyme share
// 06371, so a matching ZIP alone does not settle it), unless the ZIP typed is the result's ZIP
// and the street is the same: Mapbox names many real addresses by their postal village
// ("1 Mohegan Sun Blvd, Montville 06382" -> "1 Mohegan Sun Boulevard, Uncasville 06382"). With
// no town to compare, the match is rejected when the ZIP typed is from another area. A result
// without town or ZIP details is not a mismatch. Known trade-off: a street of the same name in
// a neighbouring town that shares the ZIP still pins ("500 Long Hill Rd, Ledyard 06340" ->
// Groton 06340).
const isInTypedTown = (match, typed) => {
  const typedTown = normalizeTown(typed?.city)
  const typedZip = normalizeZip(typed?.zipCode)
  const foundTowns = [...contextTexts(match, 'place'), ...contextTexts(match, 'locality')].map(normalizeTown)
  const foundZips = contextTexts(match, 'postcode').map(normalizeZip)

  if (typedTown && foundTowns.length) {
    if (foundTowns.some(town => sameTown(typedTown, town))) return true
    return Boolean(typedZip) && foundZips.includes(typedZip) && sameStreet(match, typed?.streetAddress)
  }
  // the first three ZIP digits only tell the region apart (063xx is south-east Connecticut,
  // 338xx central Florida), so a slightly wrong ZIP in the same area still matches
  if (typedZip && foundZips.length) return foundZips.some(zip => zip.slice(0, 3) === typedZip.slice(0, 3))
  return true
}
const noAddressNote = 'Enter the address above to pinpoint your place on the map.'
const notFoundNote = "We couldn't find that address. Click the map to place your pin."

interface MapPickerProps {
  name?: string
  onChange?(...args: unknown[]): unknown
  value?: Record<string, unknown>
}

const MapPicker = ({ value, onChange, coordinatesQuery }: MapPickerProps) => {
  // the map stays hidden until there is an address to show, unless the place already has a saved pin.
  // A pin means "we know where this is" (CVL-74): there is none until the address is pinpointed or
  // the map is clicked, and with no pin the place is saved with no location.
  const [{ zoom, center, showMap, notFound, pinned }, setState] = React.useReducer((prev, next) => ({ ...prev, ...next }), {
    zoom: [value?.coordinates ? 20 : zoomLevel],
    center: value?.coordinates || DEFAULT_MAP_CENTER_COORDINATES,
    showMap: Boolean(value?.coordinates),
    notFound: false,
    pinned: Boolean(value?.coordinates),
  })

  // a saved or hand-placed pin keeps the map showing even if the address is cleared
  const pinPlaced = useRef(Boolean(value?.coordinates))

  // cancels a pending or in-flight address lookup so it can't move a pin the user has placed
  const cancelGeocoding = useRef<() => void>(() => undefined)

  const setCenter = React.useCallback(newCenter => {
    cancelGeocoding.current()
    pinPlaced.current = true
    setState({ center: newCenter, pinned: true })
  }, [])

  const geocodingQuery = coordinatesQuery && Object.values(coordinatesQuery).filter(Boolean).join(',')
  // the address typed, read when a lookup answers; it changes only along with geocodingQuery
  const typedAddress = useRef(coordinatesQuery)
  typedAddress.current = coordinatesQuery
  const { lng: initialLng, lat: initialLat } = LngLat.convert(DEFAULT_MAP_CENTER_COORDINATES)

  // a saved place's pin stays put while its address matches the saved one. This relies on the
  // address and the saved pin arriving in the first render, which holds because the place form
  // waits for the place to load.
  const savedQuery = useRef(value?.coordinates ? geocodingQuery : undefined)
  const savedCenter = useRef(value?.coordinates)

  // wait until the address has stopped changing before geocoding and moving the map
  React.useEffect(() => {
    if (savedQuery.current !== undefined && geocodingQuery === savedQuery.current) {
      // the address is back to the saved one: a pending lookup was cancelled by the cleanup, and a
      // pin moved by a lookup goes back to the saved spot (a pin placed by hand stays), and a
      // "couldn't find" note from an address typed in between goes away
      if (!pinPlaced.current) {
        pinPlaced.current = true
        setState({ zoom: [20], center: savedCenter.current, notFound: false, pinned: true })
      } else {
        setState({ notFound: false })
      }
      return
    }

    if (geocodingQuery) {
      let request
      let cancelled = false
      const timeout = setTimeout(() => {
        request = geocoding.forwardGeocode({
          query: geocodingQuery,
          proximity: [initialLng, initialLat],
        })

        request
          .send()
          .then(response => {
            if (cancelled) return
            const bestMatch = get(response, 'body.features[0]')
            if (isTrustedMatch(bestMatch) && isInTypedTown(bestMatch, typedAddress.current)) {
              pinPlaced.current = false
              setState({ zoom: [18], center: bestMatch.center, showMap: true, notFound: false, pinned: true })
            } else {
              // no match, or not a house-level match we can trust: show the map anyway so the pin can be
              // placed by hand, and remove the pin of the address before it (CVL-74)
              pinPlaced.current = false
              setState({ showMap: true, notFound: true, pinned: false })
            }
          })
          .catch(() => {
            if (cancelled) return
            pinPlaced.current = false
            setState({ showMap: true, notFound: true, pinned: false })
          })
      }, geocodingDelay)

      const cancel = () => {
        cancelled = true
        clearTimeout(timeout)
        if (request) request.abort()
      }
      cancelGeocoding.current = cancel

      return cancel
    }

    // once the address has been cleared, hide the map again unless a pin was placed
    const timeout = setTimeout(() => {
      setState(pinPlaced.current ? { notFound: false } : { showMap: false, notFound: false, pinned: false })
    }, geocodingDelay)
    const cancel = () => clearTimeout(timeout)
    cancelGeocoding.current = cancel

    return cancel
  }, [geocodingQuery, initialLng, initialLat])

  // with no pin, the place has no location (never the map center)
  const hasLocation = Boolean(value)
  React.useEffect(() => {
    if (!pinned && hasLocation) onChange(null)
  }, [pinned, hasLocation, onChange])

  const { lng, lat } = LngLat.convert(center)

  return (
    <section>
      {showMap ? (
        <div id="map-picker">
          {notFound && <p className="map-picker-note">{notFoundNote}</p>}
          <Mapbox
            zoom={zoom}
            center={center}
            onClick={(map, event) => {
              setCenter(event.lngLat)
            }}
            style={mapStyle}
            flyToOptions={flyToOptions}
            mapOptions={mapOptions}
          >
            <NavigationControl position="top-left" />
            {pinned && <DraggableMarker setCenter={setCenter} center={center} />}
          </Mapbox>
        </div>
      ) : (
        <p className="map-picker-note">{noAddressNote}</p>
      )}
      {pinned && (
        <>
          <HiddenField name="type" value="Point" />
          <HiddenField name="coordinates.0" value={lng} />
          <HiddenField name="coordinates.1" value={lat} />
        </>
      )}
    </section>
  )
}

export default connectField(MapPicker)
